import { useState } from "react";
import api from "../services/api";
import "../styles/pos.css";
import { exportToExcel } from "../utils/exportExcel";

const Reports = () => {
  const user = JSON.parse(localStorage.getItem("user"));

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [active, setActive] = useState("sales");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const fetchReport = async () => {
    if (!from || !to) {
      alert("Select date range");
      return;
    }

    setLoading(true);
    setData(null);
    setHasGenerated(true);

    try {
      const res = await api.get(`/reports/${active}`, {
        params: { from, to }
      });
      setData(res.data);
    } catch {
      alert("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!data) {
      alert("No data to export");
      return;
    }

    let excelData = [];
    let fileName = "Report";

    if (active === "sales") {
      excelData = [
        {
          "From Date": from,
          "To Date": to,
          "Invoice Count": data.invoice_count,
          "Taxable Amount": data.taxable_total,
          "CGST": data.cgst_total,
          "SGST": data.sgst_total,
          "Grand Total": data.grand_total
        }
      ];
      fileName = "Sales_Summary";
    }

    if (active === "gst") {
      excelData = data.map(g => ({
        "GST %": g.gst_percent,
        "Taxable Amount": g.taxable,
        "CGST": g.cgst,
        "SGST": g.sgst
      }));
      fileName = "GST_Summary";
    }

    if (active === "top-products") {
      excelData = data.map(p => ({
        Product: p.name,
        Quantity: p.qty,
        Total: p.total
      }));
      fileName = "Top_Products";
    }

    if (active === "branch-sales") {
      excelData = data.map(b => ({
        Branch: b.branch_name,
        "Invoice Count": b.invoice_count,
        Total: b.total
      }));
      fileName = "Branch_Sales";
    }

    exportToExcel(excelData, `${fileName}_${from}_to_${to}`);
  };

  const isAdmin = ["ADMIN", "SUPER_ADMIN"].includes(user.role);

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Reports</h2>
      </div>

      {/* FILTER BAR */}
      <div className="card no-print">
        <div className="form-row">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />

          <select
            value={active}
            onChange={e => {
              setActive(e.target.value);
              setData(null);
              setHasGenerated(false);
            }}
          >
            <option value="sales">Sales Summary</option>
            <option value="gst">GST Summary</option>
            <option value="top-products">Top Products</option>

            {isAdmin && (
              <option value="branch-sales">Branch-wise Sales</option>
            )}
          </select>

          <button className="second-action-btn" onClick={fetchReport}>
            Generate
          </button>

          <button
            className="secondary-btn"
            onClick={() => window.print()}
            disabled={!data}
          >
            Print
          </button>

          <button
            className="secondary-btn"
            onClick={handleExport}
            disabled={!data}
          >
            ⬇ Export Excel
          </button>
        </div>
      </div>

      <br />

      {/* REPORT OUTPUT */}
      <div className="card print-area">
        {loading && <p>Loading report...</p>}

        {!loading && !data && !hasGenerated && (
          <p>Select date range and click Generate</p>
        )}

        <div style={{ marginBottom: 10 }}>
          <strong>Report:</strong>{" "}
          {active.replace("-", " ").toUpperCase()} <br />
          <strong>From:</strong> {from} &nbsp;
          <strong>To:</strong> {to}
        </div>

        {/* SALES SUMMARY */}
        {active === "sales" && data && (
          <>
            <h4>Sales Summary</h4>
            <div className="totals">
              <div><span>Total Invoices</span><span>{data.invoice_count}</span></div>
              <div><span>Taxable</span><span>₹{data.taxable_total}</span></div>
              <div><span>CGST</span><span>₹{data.cgst_total}</span></div>
              <div><span>SGST</span><span>₹{data.sgst_total}</span></div>
              <div className="grand">
                <span>Grand Total</span>
                <span>₹{data.grand_total}</span>
              </div>
            </div>
          </>
        )}

        {/* GST SUMMARY */}
        {active === "gst" && Array.isArray(data) && (
          <>
            <h4>GST Summary</h4>
            <table className="data-table">
              <thead>
                <tr>
                  <th>GST %</th>
                  <th>Taxable</th>
                  <th>CGST</th>
                  <th>SGST</th>
                </tr>
              </thead>
              <tbody>
                {data.map((g, i) => (
                  <tr key={i}>
                    <td>{g.gst_percent}%</td>
                    <td>₹{g.taxable}</td>
                    <td>₹{g.cgst}</td>
                    <td>₹{g.sgst}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* TOP PRODUCTS */}
        {active === "top-products" && Array.isArray(data) && (
          <>
            <h4>Top Products</h4>
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p, i) => (
                  <tr key={i}>
                    <td>{p.name}</td>
                    <td>{p.qty}</td>
                    <td>₹{p.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* BRANCH SALES – ADMIN ONLY */}
        {active === "branch-sales" && isAdmin && Array.isArray(data) && (
          <>
            <h4>Branch-wise Sales</h4>
            <table>
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Invoices</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.map((b, i) => (
                  <tr key={i}>
                    <td>{b.branch_name}</td>
                    <td>{b.invoice_count}</td>
                    <td>₹{b.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
